import React, { useState, useEffect } from 'react';
import { dashboardService, analyticsService } from '../services/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import {
  BarChart3,
  TrendingUp,
  TrendingDown,
  Users,
  Clock,
  AlertTriangle,
  RefreshCw,
} from 'lucide-react';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
} from 'recharts';

const COLORS = ['#4CAF50', '#66BB6A', '#FFEB3B', '#FF5722', '#81C784'];

export default function AnalyticsPage() {
  const [loading, setLoading] = useState(true);
  const [trends, setTrends] = useState([]);
  const [departmentStats, setDepartmentStats] = useState([]);
  const [anomalies, setAnomalies] = useState([]);

  useEffect(() => {
    loadAnalytics();
  }, []);

  const loadAnalytics = async () => {
    setLoading(true);
    try {
      const [trendsData, deptData, anomalyData] = await Promise.all([
        dashboardService.getAttendanceTrends(30),
        dashboardService.getDepartmentStats(),
        analyticsService.getAnomalies(),
      ]);
      setTrends(trendsData);
      setDepartmentStats(deptData);
      setAnomalies(anomalyData);
    } catch (error) {
      console.error('Failed to load analytics:', error);
      toast.error('Failed to load analytics');
    } finally {
      setLoading(false);
    }
  };

  // Calculate summary stats from trends
  const avgPresent = trends.length > 0 
    ? Math.round(trends.reduce((sum, t) => sum + t.present, 0) / trends.length)
    : 0;
  
  const avgLate = trends.length > 0
    ? Math.round(trends.reduce((sum, t) => sum + t.late, 0) / trends.length)
    : 0;

  const presentTrend = trends.length > 1
    ? ((trends[trends.length - 1]?.present || 0) - (trends[0]?.present || 0))
    : 0;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="space-y-6 fade-in">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-xl sm:text-3xl font-bold tracking-tight">Analytics</h1>
          <p className="text-muted-foreground mt-1">
            AI-powered insights and attendance patterns
          </p>
        </div>
        <Button onClick={loadAnalytics} variant="outline">
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Avg. Present</p>
                <p className="text-2xl font-bold">{avgPresent}</p>
                <div className="flex items-center mt-1 text-sm">
                  {presentTrend >= 0 ? (
                    <>
                      <TrendingUp className="w-4 h-4 text-green-500 mr-1" />
                      <span className="text-green-500">+{presentTrend}</span>
                    </>
                  ) : (
                    <>
                      <TrendingDown className="w-4 h-4 text-red-500 mr-1" />
                      <span className="text-red-500">{presentTrend}</span>
                    </>
                  )}
                  <span className="text-muted-foreground ml-1">vs last period</span>
                </div>
              </div>
              <div className="p-3 rounded-lg bg-blue-500/10 text-blue-500">
                <Users className="w-6 h-6" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Avg. Late Arrivals</p>
                <p className="text-2xl font-bold">{avgLate}</p>
                <p className="text-sm text-muted-foreground mt-1">per day</p>
              </div>
              <div className="p-3 rounded-lg bg-orange-500/10 text-orange-500">
                <Clock className="w-6 h-6" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Departments</p>
                <p className="text-2xl font-bold">{departmentStats.length}</p>
                <p className="text-sm text-muted-foreground mt-1">active</p>
              </div>
              <div className="p-3 rounded-lg bg-purple-500/10 text-purple-500">
                <BarChart3 className="w-6 h-6" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Anomalies Detected</p>
                <p className="text-2xl font-bold">{anomalies.length}</p>
                <p className="text-sm text-muted-foreground mt-1">pending review</p>
              </div>
              <div className="p-3 rounded-lg bg-red-500/10 text-red-500">
                <AlertTriangle className="w-6 h-6" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Attendance Trends */}
        <Card className="lg:col-span-2" data-testid="trends-chart">
          <CardHeader>
            <CardTitle>Attendance Trends (Last 30 Days)</CardTitle>
            <CardDescription>Daily attendance and late arrivals</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={trends}>
                  <defs>
                    <linearGradient id="colorPresent" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#4CAF50" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="#4CAF50" stopOpacity={0}/>
                    </linearGradient>
                    <linearGradient id="colorLate" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#FFEB3B" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="#FFEB3B" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E8F5E9" />
                  <XAxis 
                    dataKey="date" 
                    stroke="#66BB6A"
                    fontSize={12}
                    tickFormatter={(value) => value.slice(5)}
                  />
                  <YAxis stroke="#66BB6A" fontSize={12} />
                  <Tooltip 
                    contentStyle={{ 
                      background: '#ffffff', 
                      border: '1px solid #E8F5E9',
                      borderRadius: '8px',
                    }}
                  />
                  <Area
                    type="monotone"
                    dataKey="present"
                    stroke="#4CAF50"
                    fillOpacity={1}
                    fill="url(#colorPresent)"
                    name="Present"
                  />
                  <Area
                    type="monotone"
                    dataKey="late"
                    stroke="#FFEB3B"
                    fillOpacity={1}
                    fill="url(#colorLate)"
                    name="Late"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Department Stats */}
        <Card data-testid="department-chart">
          <CardHeader>
            <CardTitle>Attendance by Department</CardTitle>
            <CardDescription>Today's attendance breakdown</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={departmentStats} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="#27272A" />
                  <XAxis type="number" stroke="#A1A1AA" fontSize={12} />
                  <YAxis 
                    dataKey="department" 
                    type="category" 
                    stroke="#A1A1AA" 
                    fontSize={12}
                    width={80}
                    tickFormatter={(v) => v?.substring(0, 10)}
                  />
                  <Tooltip 
                    contentStyle={{ 
                      background: '#18181B', 
                      border: '1px solid #27272A',
                      borderRadius: '8px',
                    }}
                  />
                  <Bar dataKey="present" fill="#10B981" name="Present" />
                  <Bar dataKey="absent" fill="#EF4444" name="Absent" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Department Distribution Pie */}
        <Card data-testid="distribution-chart">
          <CardHeader>
            <CardTitle>Department Distribution</CardTitle>
            <CardDescription>Employee count by department</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={departmentStats}
                    dataKey="total"
                    nameKey="department"
                    cx="50%"
                    cy="50%"
                    outerRadius={80}
                    label={({ department, total }) => `${department}: ${total}`}
                  >
                    {departmentStats.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip 
                    contentStyle={{ 
                      background: '#18181B', 
                      border: '1px solid #27272A',
                      borderRadius: '8px',
                    }}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Anomalies */}
      {anomalies.length > 0 && (
        <Card data-testid="anomalies-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-red-500" />
              Detected Anomalies
            </CardTitle>
            <CardDescription>
              AI-detected attendance irregularities requiring review
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {anomalies.slice(0, 5).map((anomaly) => (
                <div 
                  key={anomaly.id}
                  className="p-4 rounded-lg border border-red-500/20 bg-red-500/5"
                >
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="font-medium">{anomaly.anomaly_type.replace(/_/g, ' ')}</p>
                      <p className="text-sm text-muted-foreground">{anomaly.description}</p>
                    </div>
                    <div className="text-right">
                      <div className="text-lg font-bold text-red-500">
                        {anomaly.risk_score}%
                      </div>
                      <p className="text-xs text-muted-foreground">Risk Score</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
